import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CommandPalette, type CommandItem } from '@teals/ui-web';
import { useThemeToggle } from '../hooks/useThemeToggle';
import { useAuth } from '../hooks/useAuth';
import { useCommandPalette } from '../hooks/useCommandPalette';

const NAV_ITEMS = [
  { to: '/agents', label: 'Agents' },
  { to: '/partners', label: 'Partners' },
  { to: '/data-sources', label: 'Data Sources' },
  { to: '/analytics', label: 'Analytics' },
];

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 1.5rem;
  max-width: 1440px;
  margin: 0 auto;
`;

const NavBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--text-primary);
  cursor: pointer;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledNavLink = styled(NavLink)`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
    text-decoration: none;
  }

  &.active {
    background: var(--accent-subtle);
    color: var(--accent);
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CmdTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-muted);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 220px;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--text-secondary);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Kbd = styled.kbd`
  font-family: inherit;
  font-size: 0.6875rem;
  padding: 2px 6px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  margin-left: auto;
  color: var(--text-muted);
`;

const ThemeToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.125rem;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`;

const UserDropdown = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 200px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  z-index: 200;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--border);
`;

const PageBody = styled.main`
  flex: 1;
  padding-bottom: 64px;

  @media (min-width: 769px) {
    padding-bottom: 0;
  }
`;

const MobileNav = styled.nav`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: var(--nav-bg);
  border-top: 1px solid var(--nav-border);
  z-index: 100;
  padding: 0 0.5rem;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
`;

const MobileNavItem = styled(NavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0.5rem;
  border-radius: 8px;
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s;

  &.active {
    color: var(--accent);
  }

  &:hover {
    color: var(--text-primary);
    text-decoration: none;
  }
`;

const MobileNavIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

const AppLayout: React.FC = () => {
  const { mode, toggle } = useThemeToggle();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isOpen, open, close } = useCommandPalette();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const paletteItems: CommandItem[] = useMemo(
    () => [
      ...NAV_ITEMS.map((item) => ({
        id: item.to,
        label: item.label,
        description: `Go to ${item.label}`,
        group: 'Navigation',
      })),
      {
        id: '/business',
        label: 'Business Center',
        description: 'Organisation management',
        group: 'Navigation',
      },
      {
        id: '/profile',
        label: 'Profile',
        description: 'Manage your profile',
        group: 'Navigation',
      },
      {
        id: '/payments',
        label: 'Payments',
        description: 'Billing and invoices',
        group: 'Navigation',
      },
      {
        id: 'action:toggle-theme',
        label: mode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode',
        description: 'Toggle light/dark theme',
        group: 'Actions',
      },
      {
        id: 'action:logout',
        label: 'Sign Out',
        description: 'Log out of your account',
        group: 'Actions',
      },
    ],
    [mode],
  );

  const handleSelect = (item: CommandItem) => {
    if (item.id === 'action:toggle-theme') {
      toggle();
    } else if (item.id === 'action:logout') {
      handleLogout();
    } else {
      navigate(item.id);
    }
    close();
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <>
      <Nav>
        <NavInner>
          <NavBrand onClick={() => navigate('/agents')}>
            <LogoIcon>
              <svg viewBox="0 0 32 32" width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="7" fill="var(--accent)" />
                <path d="M16 4Q16 16 4 16Q16 16 16 28Q16 16 28 16Q16 16 16 4Z" fill="white" />
                <circle cx="24" cy="7" r="1.8" fill="white" opacity="0.6" />
              </svg>
            </LogoIcon>
            Luminafactory
          </NavBrand>

          <NavLinks>
            {NAV_ITEMS.map((item) => (
              <StyledNavLink key={item.to} to={item.to}>
                {item.label}
              </StyledNavLink>
            ))}
          </NavLinks>

          <NavActions>
            <CmdTrigger onClick={open}>
              Search or ask...
              <Kbd>&#x2318;K</Kbd>
            </CmdTrigger>
            <ThemeToggleBtn
              onClick={toggle}
              aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
              title="Toggle theme"
            >
              {mode === 'light' ? '\u263E' : '\u2600'}
            </ThemeToggleBtn>
            <UserMenuWrapper ref={menuRef}>
              <UserAvatar
                onClick={() => setMenuOpen((prev) => !prev)}
                title="Account menu"
              >
                {user?.name
                  ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                  : 'U'}
              </UserAvatar>
              <UserDropdown $open={menuOpen}>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/business');
                  }}
                >
                  Business Center
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/profile');
                  }}
                >
                  Profile
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/payments');
                  }}
                >
                  Payments
                </DropdownItem>
                <DropdownDivider />
                <DropdownItem onClick={handleLogout}>Log Out</DropdownItem>
              </UserDropdown>
            </UserMenuWrapper>
          </NavActions>
        </NavInner>
      </Nav>

      <PageBody>
        <Outlet />
      </PageBody>

      <MobileNav>
        <MobileNavItem to="/agents">
          <MobileNavIcon>&#x1F916;</MobileNavIcon>
          Agents
        </MobileNavItem>
        <MobileNavItem to="/partners">
          <MobileNavIcon>&#x1F465;</MobileNavIcon>
          Partners
        </MobileNavItem>
        <MobileNavItem to="/data-sources">
          <MobileNavIcon>&#x1F4BE;</MobileNavIcon>
          Data
        </MobileNavItem>
        <MobileNavItem to="/analytics">
          <MobileNavIcon>&#x1F4CA;</MobileNavIcon>
          Analytics
        </MobileNavItem>
      </MobileNav>

      <CommandPalette
        isOpen={isOpen}
        onClose={close}
        onSelect={handleSelect}
        items={paletteItems}
        placeholder="Search pages or run actions..."
      />
    </>
  );
};

export default AppLayout;
